// hazardous-chemicals/pages/HazardousChemicalsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import { ChemicalRegister } from '../components/ChemicalRegister';
import { ChemicalForm } from '../components/ChemicalForm';
import * as chemApi from '../../../../utils/safety-api';
import { todayISO, uid } from '../utils/safety-utils';
import type { Chemical, ChemicalLists } from '../../../../types/chemical.types';

const DEFAULT_LISTS: ChemicalLists = {
    ghsSignal: ['Danger', 'Warning', 'None'],
    ppeOptions: ['Gloves', 'Safety Goggles', 'Respirator / P2 Mask', 'Face Shield', 'Hi-Vis Vest', 'Safety Boots', 'Hard Hat', 'Hearing Protection', 'Full Body Suit'],
    locations: ['Chemical Store A', 'Fuel Store', 'Workshop', 'Materials Yard', 'Gas Store', 'Head Office'],
};

export const HazardousChemicalsPage: React.FC = () => {
    const { currentUser } = useAuth();
    const ability = useAbility(AbilityContext);
    const currentUserName = currentUser ? (currentUser.name || currentUser.email || 'Unknown') : 'Unknown';
    const navigate = useNavigate();
    const location = useLocation();

    const [mode, setMode] = useState<'list' | 'add' | 'edit'>('list');
    const [chemicals, setChemicals] = useState<Chemical[]>([]);
    const [lists, setLists] = useState<ChemicalLists>(DEFAULT_LISTS);

    const [search, setSearch] = useState('');
    const [searchColumn, setSearchColumn] = useState<'name' | 'quantity' | 'unNumber' | 'poisonSchedule' | 'storage' | 'intendedUse' | 'location'>('name');
    const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
    const [editing, setEditing] = useState<Chemical | null>(null);

    const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.HAZARDOUS_CHEMICAL);
    const canUpdate = ability.can(ACTIONS.UPDATE, RESOURCES.HAZARDOUS_CHEMICAL);
    const canDelete = ability.can(ACTIONS.DELETE, RESOURCES.HAZARDOUS_CHEMICAL);

    // Handle navigation state from detail page (edit button passes chemical here)
    useEffect(() => {
        const state = location.state as { editChemical?: Chemical } | null;
        if (state?.editChemical && canUpdate) {
            setEditing(state.editChemical);
            setMode('edit');
            // Clear the state so refreshing doesn't re-trigger
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate, canUpdate]);

    useEffect(() => {
        (async () => {
            try {
                const [rows, settings] = await Promise.all([
                    chemApi.listChemicals(false),
                    chemApi.getChemicalSettings().catch(() => null),
                ]);
                setChemicals(rows);
                if (settings) setLists(settings);
            } catch {
                toast.error('Could not load the chemical register');
            }
        })();
    }, []);

    // Filter by active/archived tab and search column
    const visible = useMemo(() => {
        let rows = chemicals.filter((c) => (activeTab === 'active' ? !c.deleted : c.deleted));
        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter((c) => {
                const value = (c[searchColumn] || '').toString().toLowerCase();
                return value.includes(q);
            });
        }
        return rows;
    }, [chemicals, search, searchColumn, activeTab]);

    const softDelete = async (id: string) => {
        const target = chemicals.find((c) => c.id === id);
        setChemicals((prev) =>
            prev.map((c) => (c.id === id ? { ...c, deleted: true, deletedAt: todayISO(), deletedBy: currentUserName } : c)),
        );
        try {
            await chemApi.softDeleteChemical(id);
        } catch {
            toast.error('Could not archive — reverting');
            setChemicals((prev) => prev.map((c) => (c.id === id ? { ...c, deleted: false } : c)));
        }
    };

    const restore = async (id: string) => {
        setChemicals((prev) =>
            prev.map((c) => (c.id === id ? { ...c, deleted: false, deletedAt: undefined, deletedBy: undefined } : c)),
        );
        try {
            await chemApi.restoreChemical(id);
            toast.success('Chemical restored');
        } catch {
            toast.error('Could not restore — reverting');
            setChemicals((prev) => prev.map((c) => (c.id === id ? { ...c, deleted: true } : c)));
        }
    };

    const exportToPdf = async (selected: Chemical[]) => {
        try {
            const { jsPDF } = await import('jspdf');
            const autoTableMod = await import('jspdf-autotable');
            autoTableMod.applyPlugin(jsPDF);

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const now = new Date().toLocaleDateString('en-GB');

            // Title
            doc.setFontSize(18);
            doc.setTextColor(33, 33, 33);
            doc.text('Hazardous Chemical Register', 14, 20);

            // Subtitle
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Generated on ${now} · ${selected.length} chemical(s)`, 14, 28);

            // Table data
            const headers = [
                'Chemical Name', 'Quantity', 'GHS', 'UN Number', 'Poison / pH',
                'Storage', 'Intended Use', 'PPE Required', 'SDS', 'Location', 'Owner',
            ];

            const rows = selected.map((c) => [
                c.name,
                c.quantity,
                c.ghsSignal,
                c.unNumber || 'N/A',
                c.poisonSchedule || 'N/A',
                c.storage || '—',
                c.intendedUse || '—',
                c.ppeRequired || '—',
                c.sds ? 'Attached' : 'Missing',
                c.location || '—',
                c.owner || '—',
            ]);

            (doc as any).autoTable({
                head: [headers],
                body: rows,
                startY: 35,
                theme: 'striped',
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: 255,
                    fontSize: 9,
                    fontStyle: 'bold',
                },
                bodyStyles: {
                    fontSize: 8,
                    textColor: 50,
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245],
                },
                columnStyles: {
                    0: { cellWidth: 35 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 18 },
                    3: { cellWidth: 22 },
                    4: { cellWidth: 22 },
                    5: { cellWidth: 30 },
                    6: { cellWidth: 25 },
                    7: { cellWidth: 30 },
                    8: { cellWidth: 18 },
                    9: { cellWidth: 25 },
                    10: { cellWidth: 25 },
                },
                margin: { top: 35, left: 10, right: 10 },
                didDrawPage: (data: any) => {
                    // Footer with page number
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(
                        `Page ${data.pageNumber} of ${data.pageCount}`,
                        pageWidth - 20,
                        doc.internal.pageSize.getHeight() - 10,
                        { align: 'right' }
                    );
                },
            });

            doc.save(`hazardous-chemicals-${now.replace(/\//g, '-')}.pdf`);
            toast.success(`Exported ${selected.length} chemical(s) to PDF`);
        } catch (err) {
            console.error('PDF export failed:', err);
            toast.error('Failed to generate PDF export');
        }
    };

    const saveChemical = async (payload: Partial<Chemical>, sdsFile?: File) => {
        if (mode === 'edit' && editing) {
            const updated: Chemical = {
                ...editing,
                ...payload,
                lastModifiedBy: currentUserName,
                lastModifiedAt: todayISO(),
                audit: [
                    { id: uid('audit'), action: 'Chemical record updated', who: currentUserName, date: todayISO() },
                    ...editing.audit,
                ],
            } as Chemical;
            setChemicals((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            setMode('list');
            setEditing(null);
            try {
                await chemApi.updateChemical(updated.id, updated);
                toast.success('Chemical updated');
            } catch {
                toast.error('Saved locally — could not reach server');
            }
        } else {
            // Remove client-generated id before sending to server — DB auto-generates it
            const { id, ...payloadWithoutId } = payload as Chemical;
            const created = {
                ...payloadWithoutId,
                lastModifiedBy: currentUserName,
                lastModifiedAt: todayISO(),
                audit: [
                    { id: uid('audit'), action: 'Chemical added to register', who: currentUserName, date: todayISO() },
                    ...(payloadWithoutId.audit || []),
                ],
            };
            // Use a temporary client id for optimistic UI
            const clientId = uid('chem');
            const optimisticChemical: Chemical = { ...created, id: clientId } as Chemical;
            setChemicals((prev) => [optimisticChemical, ...prev]);
            setMode('list');
            try {
                const saved = await chemApi.createChemical(created);
                if (saved && saved.id) {
                    setChemicals((prev) => prev.map((c) => (c.id === clientId ? saved : c)));
                    toast.success(`${created.name} registered`);
                    // Upload SDS if a file was selected during add
                    if (sdsFile) {
                        try {
                            const sds = await chemApi.uploadSds(saved.id, sdsFile);
                            setChemicals((prev) =>
                                prev.map((c) => (c.id === saved.id ? { ...c, sds } : c)),
                            );
                            toast.success('SDS uploaded');
                        } catch (sdsErr: any) {
                            toast.error(sdsErr.message || 'Failed to upload SDS');
                        }
                    }
                } else {
                    throw new Error('Server returned invalid response');
                }
            } catch (err: any) {
                console.error('Create chemical failed:', err);
                toast.error(`${created.name} saved locally — server error`);
            }
        }
    };

    if (mode === 'add' && !canCreate) {
        return (
            <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
                <div className="max-w-[1500px] mx-auto w-full p-8 text-center">
                    <h1 className="text-xl font-medium text-muted-foreground">Access Denied</h1>
                    <p className="text-sm text-muted-foreground mt-2">You do not have permission to add hazardous chemicals.</p>
                    <button
                        onClick={() => setMode('list')}
                        className="mt-4 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                    >
                        Back to Register
                    </button>
                </div>
            </div>
        );
    }

    if (mode === 'edit' && !canUpdate) {
        return (
            <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
                <div className="max-w-[1500px] mx-auto w-full p-8 text-center">
                    <h1 className="text-xl font-medium text-muted-foreground">Access Denied</h1>
                    <p className="text-sm text-muted-foreground mt-2">You do not have permission to edit hazardous chemicals.</p>
                    <button
                        onClick={() => {
                            setMode('list');
                            setEditing(null);
                        }}
                        className="mt-4 h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                    >
                        Back to Register
                    </button>
                </div>
            </div>
        );
    }

    if (mode === 'add' || mode === 'edit') {
        return (
            <ChemicalForm
                initial={mode === 'edit' ? editing : null}
                lists={lists}
                onCancel={() => {
                    setMode('list');
                    setEditing(null);
                }}
                onSave={saveChemical}
                onUploadSds={async (chemicalId, file) => {
                    const sds = await chemApi.uploadSds(chemicalId, file);
                    setChemicals((prev) =>
                        prev.map((c) => (c.id === chemicalId ? { ...c, sds } : c)),
                    );
                }}
            />
        );
    }

    return (
        <ChemicalRegister
            chemicals={visible}
            search={search}
            onSearch={setSearch}
            searchColumn={searchColumn}
            onSearchColumn={(col) => {
                setSearchColumn(col as any);
                setSearch('');
            }}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onAdd={canCreate ? () => setMode('add') : undefined}
            onUploadSds={async (chemicalId, file) => {
                const sds = await chemApi.uploadSds(chemicalId, file);
                setChemicals((prev) =>
                    prev.map((c) => (c.id === chemicalId ? { ...c, sds } : c)),
                );
                toast.success('SDS uploaded');
            }}
            onExport={exportToPdf}
            onEdit={canUpdate ? (c) => {
                setEditing(c);
                setMode('edit');
            } : undefined}
            onArchive={canDelete ? softDelete : undefined}
            onRestore={canUpdate ? restore : undefined}
        />
    );
};

export default HazardousChemicalsPage;
